<template>
  <div class="header header--region" :style="{ backgroundColor: region.bg_color }" v-if="region">
    <div class="header-region" :style="{ backgroundColor: region.bg_color }">
      <div class="header-region__content">
        <section v-reveal>
          <h1 class="header-region__title">{{ region.name }}</h1>
          <div class="header-region__date">
            <template v-if="date">{{ date }} | {{ region.time }}</template>
            <template v-else> À venir </template>
          </div>

          <div class="header-region__notes">
            <div class="header-region__notes-note"><Icon id="geo" /> {{ region.location }}</div>
            <div class="header-region__notes-note"><Icon id="price" /> {{ region.pricing }}</div>
          </div>
        </section>

        <section class="header-region__end" v-reveal>
          <div v-if="region.presenter_id" class="header-region__presenter">
            <div>Évènement présenté par</div>
            <div>
              <img :src="presenterLogo" width="150" height="150" alt="presenter" />
            </div>
          </div>

          <button
            class="button"
            :style="{ backgroundColor: region.accent_color }"
            @click="scrollToRef('form', 'start')"
          >
            INSCRIS-TOI !
          </button>
        </section>
      </div>
    </div>

    <SiteNav :sticky="false" @burger="burger" />

    <div class="header-region__image" :class="imageClass">
      <transition name="fade-fast">
        <img :src="region.home.header.image.original" alt="illustration" :key="region.name" />
      </transition>
    </div>
  </div>

  <div class="content -over" v-if="region">
    <section class="section">
      <div class="section-region">
        <div class="section-region__content" v-reveal>
          <h2 class="section__title">{{ region.title }}</h2>
          <p class="section__text -region">{{ region.description }}</p>
          <div class="section-region__hydro" v-if="false">
            <div class="section-region__collab">En collaboration avec</div>
            <div>
              <img src="/images/ulaval.png" width="200" height="100" alt="ulaval" />
            </div>
          </div>
        </div>

        <div class="section-region__content" v-if="region" v-reveal>
          <div class="section-region__box" :style="{ backgroundColor: region.form_color }">
            <div class="section-region__box-item" v-for="(item, key) in schedule" :key="key">
              <div class="section-region__box-time">{{ item.time }}</div>
              <div>
                {{ item.name }}
                <div class="section-region__box-note" v-if="item.subtitle">{{ item.subtitle }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <SwiperImage :images="images" :region-id="region.id" />

    <div ref="form" id="form" />

    <section class="section -center">
      <h2 class="section__title" ref="form-title" v-reveal>{{ region.forms_title }}</h2>
      <p class="section__text -lg" v-reveal>{{ region.forms_description }}</p>
      <div class="partners__tabs form__tabs" v-if="Object.values(forms).length">
        <button
          class="partners__tabs-tab"
          :class="{ '-active': tab === form.id }"
          v-for="form in forms"
          :key="form.id"
          @click="changeForm(form.id)"
        >
          {{ form.title }}
        </button>
      </div>

      <transition name="fade-fast" mode="out-in">
        <div v-if="sent" class="form__sent">
          <div class="form__sent-icon" :style="{ backgroundColor: region.accent_color }">
            <Icon id="checkmark" />
          </div>
          <b class="form__sent-title">Nous avons bien reçu ton formulaire d’inscription!</b>
          <p class="section__text -sm">
            Surveille ta boîte de réception pour recevoir ta confirmation. N’oublie pas de regarder
            dans tes courriers indésirables si tu ne le vois pas.
          </p>
        </div>
        <form
          class="form"
          submit.prevent=""
          v-else-if="
            currentFormSection && checkDate(currentForm.published_from, currentForm.published_to)
          "
          :key="`form-${tab}-${section}`"
        >
          <div class="form-row" v-for="line in currentFormSection.lines" :key="line.key">
            <template v-for="field in line.attributes.fields" :key="field.key">
              <template v-if="field.layout === 'dropdown_field'">
                <label class="form-input">
                  <span> {{ field.attributes.name }} </span>
                  <div class="input__select">
                    <select
                      v-model="answers[field.attributes.attribute]"
                      class="input"
                      :style="{ backgroundColor: inputColor }"
                    >
                      <option disabled :value="null">{{ field.attributes.description }}</option>
                      <option
                        v-for="option in field.attributes.options"
                        :key="option.key"
                        :value="option.attributes.value"
                      >
                        {{ option.attributes.title }}
                      </option>
                    </select>
                  </div>
                </label>
              </template>

              <template
                v-else-if="field.layout === 'radio_field' || field.layout === 'multiselect_field'"
              >
                <div class="form__radio">
                  <label class="form-input">
                    <span
                      :class="{
                        '-form-error': errorKeys.find((key) => key === field.attributes.attribute),
                      }"
                    >
                      {{ field.attributes.name }}
                    </span>
                    <p class="form-input__description">
                      {{ field.attributes.description }}
                    </p>
                  </label>

                  <div
                    v-for="(option, key) in field.attributes.options"
                    :key="key"
                    class="input__radio"
                    :class="{
                      '-active':
                        option.attributes.value === answers[field.attributes.attribute] ||
                        answers[field.attributes.attribute]?.includes(option.attributes.value),
                    }"
                    :style="{ backgroundColor: inputColor }"
                    @click="handleOptionClick(field, option)"
                  >
                    <div
                      class="input__radio-circle"
                      :class="{
                        '-active':
                          option.attributes.value === answers[field.attributes.attribute] ||
                          answers[field.attributes.attribute]?.includes(option.attributes.value),
                        '-square': field.layout === 'multiselect_field',
                      }"
                    >
                      <Icon
                        v-if="
                          option.attributes.value === answers[field.attributes.attribute] ||
                          answers[field.attributes.attribute]?.includes(option.attributes.value)
                        "
                        id="checkmark"
                      />
                    </div>

                    <div class="input__radio-label">
                      {{ option.attributes.title }}
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="field.layout === 'bool_field'">
                <label class="form-input">
                  <span
                    :class="{
                      '-form-error': errorKeys.find((key) => key === field.attributes.attribute),
                    }"
                  >
                    {{ field.attributes.name }}
                  </span>
                  <p class="form-input__description">
                    {{ field.attributes.description }}
                  </p>

                  <div class="input__yesno">
                    <div
                      @click="toggleBoolField(field)"
                      class="input__yesno-card"
                      :style="{ backgroundColor: inputColor }"
                    >
                      <div
                        class="input__radio-circle"
                        :class="{ '-active': answers[field.attributes.attribute] }"
                      >
                        <Icon v-if="answers[field.attributes.attribute]" id="checkmark" />
                      </div>
                      <div class="input__yesno-label">Oui</div>
                    </div>

                    <div
                      @click="setBoolFieldFalse(field)"
                      class="input__yesno-card"
                      :style="{ backgroundColor: inputColor }"
                      v-if="!field.attributes.only_yes"
                    >
                      <div
                        class="input__radio-circle"
                        :class="{ '-active': answers[field.attributes.attribute] === false }"
                      >
                        <Icon v-if="answers[field.attributes.attribute] === false" id="checkmark" />
                      </div>
                      <div class="input__yesno-label">Non</div>
                    </div>
                  </div>
                </label>
              </template>

              <template v-else-if="field.layout === 'date_field' || field.layout === 'phone_field'">
                <label class="form-input">
                  <span>
                    {{ field.attributes.name }}
                    <span class="form-input__facultatif" v-if="!field.attributes.required"
                      >(facultatif)</span
                    >
                  </span>
                  <input
                    :type="field.layout === 'phone_field' ? 'tel' : 'text'"
                    class="input"
                    :class="{
                      '-error': errorKeys.find((key) => key === field.attributes.attribute),
                    }"
                    v-maska
                    :data-maska="field.layout === 'phone_field' ? '(###) ###-####' : '##/##/####'"
                    :style="{ backgroundColor: inputColor }"
                    v-model="answers[field.attributes.attribute]"
                    :placeholder="
                      field.attributes.description || field.layout === 'date_field'
                        ? 'jj/mm/aaaa'
                        : '(000) 000-0000'
                    "
                    @input="input(field.attributes.attribute)"
                  />
                </label>
              </template>

              <template v-else>
                <label class="form-input">
                  <span>
                    {{ field.attributes.name }}
                    <span class="form-input__facultatif" v-if="!field.attributes.required"
                      >(facultatif)</span
                    >
                  </span>
                  <p class="form-input__description" v-if="field.attributes.description">
                    {{ field.attributes.description }}
                  </p>

                  <input
                    type="text"
                    class="input"
                    :class="{
                      '-error': errorKeys.find((key) => key === field.attributes.attribute),
                    }"
                    :style="{ backgroundColor: inputColor }"
                    v-model="answers[field.attributes.attribute]"
                    @input="input(field.attributes.attribute)"
                  />
                </label>
              </template>
            </template>
          </div>
        </form>

        <div v-else class="form__soon">
          <div class="form__soon-text">La période d’inscription est fermée.</div>
        </div>
      </transition>

      <div
        class="form__actions"
        v-if="
          !sent &&
          Object.values(forms).length &&
          checkDate(currentForm.published_from, currentForm.published_to)
        "
      >
        <div class="row">
          <div
            class="form__progress-bar"
            :style="{ backgroundColor: inputColor }"
            v-if="progressPercentage !== -1"
          >
            <div
              class="form__progress-bar__line"
              :style="{ backgroundColor: region.accent_color, width: `${progressPercentage}%` }"
            ></div>
          </div>
          <div class="form__progress-bar__label" v-if="progressPercentage !== -1">
            {{ Math.floor(progressPercentage) }} %
          </div>
        </div>

        <transition name="fade-fast" mode="out-in">
          <div class="row" :key="section">
            <button
              class="button -outline"
              :style="{ color: region.accent_color, borderColor: region.accent_color }"
              @click="back()"
              v-if="section >= 1"
            >
              PRÉCÉDENT
            </button>
            <button
              class="button -white"
              :style="{ backgroundColor: region.accent_color }"
              @click="next()"
              :disabled="!canNext"
            >
              {{ section + 1 <= currentFormSections.length - 1 ? 'SUIVANT' : 'SOUMETTRE' }}
            </button>
          </div>
        </transition>
      </div>
    </section>

    <template v-for="(group, index) in partners" :key="index">
      <section v-if="group.partners?.length" class="section -center">
        <h2 class="section__title" v-reveal>{{ group.title || '' }}</h2>
        <p class="section__text">{{ group.subtitle || '' }}</p>
        <div class="row -wrap -center -desktop" v-reveal>
          <a
            v-for="(partner, key) in group.partners"
            :key="partner.id"
            target="_blank"
            :href="partner.website"
            class="section-region__hydro"
            v-reveal="{ delay: key * 70 }"
          >
            <img
              :src="partner.logo.sm || partner.logo.original"
              width="200"
              height="200"
              :alt="partner.name + ' logo'"
            />
          </a>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { vMaska } from 'maska/vue'

import SiteNav from '@/components/SiteNav.vue'
import SwiperImage from '@/components/SwiperImage.vue'
import { setMeta } from '@/helpers/metaHelpers.js'

export default {
  name: 'Region',
  components: { SwiperImage, SiteNav },
  directives: { maska: vMaska },
  inject: ['mq'],
  data() {
    return {
      recaptcha: false,
      sent: false,
      tab: 1,
      section: 0,
      answers: {},
      errors: {},
      submitted: false,
    }
  },
  watch: {
    '$route.params.region': {
      handler() {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        this.tab = Object.values(this.forms)[0]?.id
        this.section = 0
        this.answers = {}
      },
      immediate: true,
    },
    region: {
      handler(to) {
        if (to?.seo) setMeta(to.seo)
      },
      immediate: true,
    },
    currentForm: {
      handler() {
        this.initValues()
      },
      immediate: true,
    },
  },
  mounted() {
    if (!this.region?.name) this.$router.push({ name: 'not-found' })
    setTimeout(() => {
      this.tab = Object.values(this.forms)[0]?.id
    }, 300)
  },
  computed: {
    images() {
      return this.region.slider_images.map((img) => img.lg)
    },
    schedule() {
      if (this.region.schedules) {
        return Object.values(this.region.schedules).toSorted((a, b) => {
          return a.order - b.order
        })
      }
      return []
    },
    presenterLogo() {
      return this.$beet.partners[this.region.presenter_id].logo?.original
    },
    canNext() {
      if (this.submitted) return false
      if (this.currentFormSection?.lines?.length > 0) {
        let can = true
        this.currentFormSection.lines.forEach((line) => {
          line.attributes.fields.forEach((field) => {
            if (['courriel', 'adresse_courriel'].includes(field.attributes.attribute)) {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              if (!emailRegex.test(this.answers[field.attributes.attribute])) can = false
            }
            if (field.attributes.required && this.answers[field.attributes.attribute] === null)
              can = false
          })
        })
        return can
      } else {
        return false
      }
    },
    errorKeys() {
      if (this.errors.errors)
        return Object.keys(this.errors.errors).map((key) => key.replace('fields.', ''))
      return []
    },
    progressPercentage() {
      if (this.currentFormSections.length === 0 || this.currentFormSections.length === 1) return -1
      if (this.section === 0) return 0
      return ((this.section + 1) / this.currentFormSections.length) * 100
    },
    forms() {
      const forms = {}
      if (this.region?.forms) {
        Object.values(this.region.forms).forEach((form) => {
          if (
            form.published_from &&
            new Date(form.published_from).getTime() < new Date().getTime()
          ) {
            forms[form.id] = form
          }
        })
      }

      if (Object.values(forms).length > 0) return this.region.forms

      return forms
    },
    currentForm() {
      return this.forms[this.tab] || {}
    },
    currentFormSections() {
      return this.currentForm?.fields || []
    },
    currentFormSection() {
      return this.currentFormSections[this.section]?.attributes || []
    },
    date() {
      if (this.region.date) {
        return new Date(this.region.date).toLocaleDateString('fr', {
          day: 'numeric',
          month: 'long',
        })
      }
      return null
    },

    inputColor() {
      return this.region.form_color || '#f1f1f1'
    },
    region() {
      let city = {}
      if (this.$beet.cities && this.$route.params.region) {
        const cities = Object.values(this.$beet.cities)
        city = cities.find(
          ({ name }) => name.toLowerCase() === this.$route.params.region.toLowerCase(),
        )
      }
      return city
    },
    imageClass() {
      if (this.region.name === 'Québec') return '-qc'
      if (this.region.name === 'Sherbrooke') return '-sh'
      else return ''
    },
    partners() {
      let data = [
        { ...this.region.main_partners },
        { ...this.region.announcer_partners },
        { ...this.region.local_partners },
      ]

      data.forEach((group) => {
        if (group.ids && group.ids.length) {
          group.partners = group.ids.map((id) => {
            return this.$beet.partners[Number(id)]
          })
          delete group.ids
        }
      })
      return data
    },
  },
  methods: {
    checkDate(from, to) {
      const fromDate = new Date(from).getTime()
      const toDate = new Date(to).getTime()
      const now = new Date().getTime()
      return fromDate < now && toDate > now
    },
    radioClick(field, option) {
      if (field.layout === 'radio_field') {
        this.answers[field.attributes.attribute] = option.attributes.value
      } else {
        if (!this.answers[field.attributes.attribute]) this.answers[field.attributes.attribute] = []
        if (this.answers[field.attributes.attribute].includes(option.attributes.value)) {
          this.answers[field.attributes.attribute] = this.answers[
            field.attributes.attribute
          ].filter((val) => val !== option.attributes.value)
        } else {
          this.answers[field.attributes.attribute].push(option.attributes.value)
        }
      }
    },
    handleOptionClick(field, option) {
      this.radioClick(field, option)
      this.input(field.attributes.attribute)
    },
    toggleBoolField(field) {
      this.answers[field.attributes.attribute] = !this.answers[field.attributes.attribute]
      this.input(field.attributes.attribute)
    },
    setBoolFieldFalse(field) {
      this.answers[field.attributes.attribute] = false
      this.input(field.attributes.attribute)
    },
    showRecaptcha() {
      if (!this.recaptcha)
        document.getElementsByClassName('grecaptcha-badge')[0].style.visibility = 'visible'
    },
    initValues() {
      this.currentFormSections.forEach((section) => {
        section.attributes.lines.forEach((line) => {
          Object.values(line.attributes.fields).forEach((field) => {
            this.answers[field.attributes.attribute] = null
          })
        })
      })
    },
    input(key) {
      this.showRecaptcha()
      if (this.errors.errors) {
        const fieldKey = `fields.${key}`
        if (this.errors.errors[fieldKey]?.length) {
          delete this.errors.errors[fieldKey]
        }
      }
    },
    submit() {
      this.submitted = true
      this.$recaptcha().then(() => {
        this.$store
          .dispatch('submitForm', {
            city_id: this.region.id,
            form_id: this.currentForm.id,
            fields: this.answers,
          })
          .then(() => {
            this.sent = true
            this.submitted = false
          })
          .catch((err) => {
            this.errors = err
            this.submitted = false
            if (this.errors.errors) {
              const key = Object.keys(this.errors.errors)[0]
              this.currentFormSections.forEach((section, index) => {
                section.attributes.lines.forEach((line) => {
                  Object.values(line.attributes.fields).forEach((field) => {
                    if (field.attributes.attribute === key.replace('fields.', '')) {
                      this.section = index
                      this.scrollToRef('form-title', 'start')
                      return
                    }
                  })
                })
              })
            }
          })
      })
    },
    burger() {
      this.$emit('burger')
    },
    changeForm(key) {
      this.tab = key
      this.section = 0
    },
    next() {
      if (this.section + 1 <= this.currentFormSections.length - 1) {
        this.section += 1
        this.scrollToRef('form-title', 'start')
      } else {
        this.submit()
      }
    },
    back() {
      if (this.section >= 1) {
        this.section -= 1
        this.scrollToRef('form-title', 'start')
      }
    },
    scrollToRef(key, position = 'center') {
      const el = this.$refs[key]
      el.scrollIntoView({ behavior: 'smooth', block: position })
    },
  },
}
</script>
